
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { trackToolUsage as trackToolUsageApi } from "@/services/d1Service";
import { getAuthHeader } from "@/services/authService";

/**
 * Hook to track tool usage
 * @param toolName The name of the tool
 * @param postId Optional post ID if the tool is used within a blog post
 */
export const useToolUsageTracker = (
  toolName: string, 
  postId?: string
) => {
  const [instanceId] = useState(() => uuidv4());

  const trackToolUsage = async (params: { 
    action: string;
    metadata?: Record<string, any>;
  }) => {
    try {
      // Store locally first
      const localKey = `${toolName}_${instanceId}_uses`;
      const currentUses = localStorage.getItem(localKey) || '0';
      const newUses = parseInt(currentUses, 10) + 1;
      localStorage.setItem(localKey, newUses.toString());
      
      // Increment view count in local analytics
      const analyticsKey = 'blog_tool_usage';
      const analytics = JSON.parse(localStorage.getItem(analyticsKey) || '{}');
      analytics[toolName] = (analytics[toolName] || 0) + 1;
      localStorage.setItem(analyticsKey, JSON.stringify(analytics));
      
      // Log usage to console for debugging
      console.log(`Tool usage tracked: ${toolName} (${params.action}), total uses: ${newUses}`);
      
      await trackToolUsageApi({
        toolName,
        instanceId,
        postId: postId || undefined,
        metadata: {
          action: params.action,
          local_count: newUses,
          browser_info: navigator.userAgent,
          screen_width: window.innerWidth,
          screen_height: window.innerHeight,
          ...params.metadata,
        },
      });
    } catch (error) {
      // Fail silently if there's an error tracking usage
      console.log('Error tracking tool usage:', error);
    }
  };

  return { instanceId, trackToolUsage };
};

/**
 * Track the usage of a tool
 * @param toolName The name of the tool
 * @param instanceId A unique ID for the tool instance
 * @param postId Optional post ID if the tool is used within a blog post
 */
export const trackToolUsage = async (
  toolName: string, 
  instanceId: string, 
  postId?: string
): Promise<void> => {
  try {
    // Store locally first
    const localKey = `${toolName}_${instanceId}_uses`;
    const currentUses = localStorage.getItem(localKey) || '0';
    const newUses = parseInt(currentUses, 10) + 1;
    localStorage.setItem(localKey, newUses.toString());
    
    // Increment view count in local analytics
    const analyticsKey = 'blog_tool_usage';
    const analytics = JSON.parse(localStorage.getItem(analyticsKey) || '{}');
    analytics[toolName] = (analytics[toolName] || 0) + 1;
    localStorage.setItem(analyticsKey, JSON.stringify(analytics));
    
    // Log usage to console for debugging
    console.log(`Tool usage tracked: ${toolName} (instance: ${instanceId}), total uses: ${newUses}`);
    
    await trackToolUsageApi({
      toolName,
      instanceId,
      postId: postId || undefined,
      metadata: {
        local_count: newUses,
        browser_info: navigator.userAgent,
        screen_width: window.innerWidth,
        screen_height: window.innerHeight,
      },
    });
  } catch (error) {
    // Fail silently if there's an error tracking usage
    console.log('Error tracking tool usage:', error);
  }
};

/**
 * Get the usage count for a tool
 * @param toolName The name of the tool
 * @returns The number of times the tool has been used
 */
export const getToolUsageCount = (toolName: string): number => {
  try {
    const analyticsKey = 'blog_tool_usage';
    const analytics = JSON.parse(localStorage.getItem(analyticsKey) || '{}');
    return analytics[toolName] || 0;
  } catch (error) {
    console.error('Error getting tool usage count:', error);
    return 0;
  }
};

/**
 * Get the total usage count for a tool across all instances from the database
 * @param toolName The name of the tool
 * @returns Promise with the number of times the tool has been used
 */
export const getDatabaseToolUsageCount = async (toolName: string): Promise<number> => {
  try {
    const response = await fetch(`/api/admin/tool-usage-stats`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) return 0;
    const stats = await response.json();
    const tool = stats.find((entry: { toolName: string }) => entry.toolName === toolName);
    return tool?.totalUsage || 0;
  } catch (error) {
    console.error('Error getting tool usage count from database:', error);
    return 0;
  }
};
